import type {Questionnaire} from '@refinio/one.models/lib/models/QuestionnaireModel.js';

export const ProfileQuestionnaireEN: Questionnaire = {
    resourceType: 'Questionnaire',
    language: 'en',
    url: 'http://heyfreeda.com/questionnaire/profile/en',
    name: 'ProfileQuestionnaire',
    title: 'Personal Profile',
    status: 'active',
    item: [
        // ══════════════════════════════════════════════
        // Introduction
        // ══════════════════════════════════════════════
        {
            linkId: 'intro',
            text: 'Introduction',
            type: 'group',
            disabledDisplay: 'protected',
            item: [
                {
                    linkId: 'introText',
                    text: 'Dear participant,\n\nwe want to support you in the best possible way on your journey. To do this, we would like to create a personal profile about you that shows who you are, what makes you special, and which path you would like to take. It can help us talk to potential training companies, partners or supporters in Germany about your prospects. To make sure this profile paints a clear and credible picture of you, we have prepared this questionnaire.',
                    type: 'display',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'introWhyText',
                    text: 'Why is this important?\n\nMany applications sound very similar. They often contain general sentences like: "I am hard-working, punctual and enjoy learning." That is not wrong, but it often does not leave a lasting impression.\n\nMore helpful are concrete and personal answers. For example, if you describe what your daily life looks like, what motivates you, or why a certain path is important to you, it creates a better picture of you as a person. This makes it easier to understand what makes you special and why you want to take this step.',
                    type: 'display',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'introHintsText',
                    text: 'Important before you start\n\nThere are no wrong answers. You can fill out the questionnaire in English. What matters most is that the thoughts and words come from you. We will help with language refinements later.\n\nThese tips can help you fill it out:\n\n\u2022 Be as specific as possible. Short answers are fine. It becomes even more helpful if you briefly add why something is important to you or give a small example.\n\u2022 Write honestly and in your own words. You do not need to write what you think we want to hear. The most helpful answers are those that truly fit you.\n\u2022 Only share what feels right for you. If there were difficulties or obstacles on your path, you are welcome to mention them. But you do not have to share anything personal that you do not want to talk about.',
                    type: 'display',
                    disabledDisplay: 'protected'
                }
            ]
        },

        // ══════════════════════════════════════════════
        // A. Factual Questions
        // ══════════════════════════════════════════════
        {
            linkId: 'facts',
            text: 'Factual Questions',
            type: 'group',
            disabledDisplay: 'protected',
            item: [
                {
                    linkId: 'nameAgeLocation',
                    text: 'What is your name, how old are you and where do you currently live?',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'education',
                    text: 'What have you completed so far in terms of school, university or vocational training?',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'languageSkills',
                    text: 'What language skills do you currently have, especially in German?',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'certificates',
                    text: 'Do you have an official German language certificate or other qualifications?',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'whyGermany',
                    text: 'Why do you want to take this path \u2013 training, studies or work \u2013 and why in Germany?',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'nextStep',
                    text: 'What would be the next important step for you?',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'idealStart',
                    text: 'When would you ideally start a training programme, job or studies?',
                    type: 'string',
                    disabledDisplay: 'protected'
                }
            ]
        },

        // ══════════════════════════════════════════════
        // B. Core Questions for Your Profile
        // ══════════════════════════════════════════════
        {
            linkId: 'coreProfile',
            text: 'Core Questions for Your Profile',
            type: 'group',
            disabledDisplay: 'protected',
            item: [
                {
                    linkId: 'origin',
                    text: 'Your story begins where you come from \u2013 tell us about it.',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'educationPath',
                    text: 'What has your educational path looked like so far?',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'turningPoint',
                    text: 'Was there a moment when you realised that you wanted more for your future?',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'typicalDay',
                    text: 'Describe a normal day in your life.',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'roleModel',
                    text: 'Who has influenced you the most in your life \u2013 and why?',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'motivation',
                    text: 'What motivates you, even when things are difficult?',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'proud',
                    text: 'What are you proud of?',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'future',
                    text: 'How do you imagine your future?',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'aboutYou',
                    text: 'What should we definitely know about you?',
                    type: 'string',
                    disabledDisplay: 'protected'
                }
            ]
        },

        // ══════════════════════════════════════════════
        // C. A Little Bit About You
        // ══════════════════════════════════════════════
        {
            linkId: 'personal',
            text: 'A Little Bit About You',
            type: 'group',
            disabledDisplay: 'protected',
            item: [
                {
                    linkId: 'favouriteFood',
                    text: 'What is your favourite food?',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'threeWords',
                    text: 'What three words would your friends or family use to describe you?',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'dailyJoy',
                    text: 'What do you look forward to every day?',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'favouriteMedia',
                    text: 'Which book, song or film do you particularly like?',
                    type: 'string',
                    disabledDisplay: 'protected'
                }
            ]
        },

        // ══════════════════════════════════════════════
        // Photos
        // ══════════════════════════════════════════════
        {
            linkId: 'photos',
            text: 'Photos',
            type: 'group',
            disabledDisplay: 'protected',
            item: [
                {
                    linkId: 'photosText',
                    text: 'Please also send us recent photos that show you or your daily life. For example:\n\n\u2022 Portrait photos of you\n\u2022 Pictures from your daily life\n\u2022 Photos from your town or surroundings\n\u2022 Pictures from school, university, courses or studying\n\nPlease only send photos that you feel comfortable with and that we may use. Other people should only appear in the pictures if they are okay with it.\n\nPlease send the photos via WhatsApp to Janine: +49 15141472058',
                    type: 'display',
                    disabledDisplay: 'protected'
                }
            ]
        }
    ]
};
